const HEIGHT_ONLY_RESIZE_THRESHOLD_PX = 96;
const WIDTH_RESIZE_THRESHOLD_PX = 2;
const MAX_DIAGNOSTIC_EVENTS = 80;

export function shouldResizeRenderer(previous, next) {
  if (!previous) return true;
  const widthDelta = Math.abs(next.width - previous.width);
  const heightDelta = Math.abs(next.height - previous.height);
  const orientationChanged = (previous.width >= previous.height) !== (next.width >= next.height);
  return orientationChanged
    || widthDelta >= WIDTH_RESIZE_THRESHOLD_PX
    || heightDelta >= HEIGHT_ONLY_RESIZE_THRESHOLD_PX;
}

export class RafResizeCoordinator {
  constructor(callback, {
    requestFrame = (run) => requestAnimationFrame(run),
    cancelFrame = (frame) => cancelAnimationFrame(frame),
  } = {}) {
    this.callback = callback;
    this.requestFrame = requestFrame;
    this.cancelFrame = cancelFrame;
    this.frame = null;
    this.latestSource = null;
  }

  schedule(source = 'resize-observer') {
    this.latestSource = source;
    if (this.frame !== null) return;
    this.frame = this.requestFrame(() => {
      this.frame = null;
      const latestSource = this.latestSource;
      this.latestSource = null;
      this.callback(latestSource);
    });
  }

  dispose() {
    if (this.frame !== null) this.cancelFrame(this.frame);
    this.frame = null;
    this.latestSource = null;
  }
}

export function createViewDiagnostics(label) {
  const state = {
    label,
    resizeEvents: 0,
    setSizeCalls: 0,
    fitCameraCalls: 0,
    contextLost: 0,
    contextRestored: 0,
    events: [],
  };
  if (import.meta.env.DEV) {
    const registryKey = '__cubePathViewDiagnostics';
    const registry = globalThis[registryKey] ?? [];
    registry.push(state);
    globalThis[registryKey] = registry;
  }

  return {
    state,
    record(type, details = {}) {
      if (type === 'resize') state.resizeEvents += 1;
      else if (type === 'renderer-set-size') state.setSizeCalls += 1;
      else if (type === 'fit-camera') state.fitCameraCalls += 1;
      else if (type === 'context-lost') state.contextLost += 1;
      else if (type === 'context-restored') state.contextRestored += 1;
      const event = {
        type,
        at: Math.round(performance.now()),
        ...details,
        counts: {
          resizeEvents: state.resizeEvents,
          setSizeCalls: state.setSizeCalls,
          fitCameraCalls: state.fitCameraCalls,
          contextLost: state.contextLost,
          contextRestored: state.contextRestored,
        },
      };
      state.events.push(event);
      if (state.events.length > MAX_DIAGNOSTIC_EVENTS) state.events.shift();
      if (import.meta.env.DEV) console.debug(`[${label}] ${type}`, event);
      return event;
    },
  };
}

export function monitorWebGLContext(canvas, diagnostics) {
  let lost = false;
  const handleContextLost = (event) => {
    event.preventDefault();
    lost = true;
    const details = { statusMessage: event.statusMessage || 'WebGL context lost' };
    diagnostics.record('context-lost', details);
    console.error(`[${diagnostics.state.label}] WebGL context lost`, details);
  };
  const handleContextRestored = () => {
    lost = false;
    diagnostics.record('context-restored');
    console.info(`[${diagnostics.state.label}] WebGL context restored`);
  };
  canvas.addEventListener('webglcontextlost', handleContextLost, false);
  canvas.addEventListener('webglcontextrestored', handleContextRestored, false);
  return {
    get lost() { return lost; },
    dispose() {
      canvas.removeEventListener('webglcontextlost', handleContextLost, false);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored, false);
    },
  };
}
